import { useState, useEffect } from 'react';
import {
  getAllResults, getAllUsers, getCompletedResults, getAvgScore, getRisk,
  inviteUser, getOrgs, createOrg, updateOrg, deleteOrg, getOrgUsers, removeOrgUser,
  deleteEnterpriseUser, deletePublicUser, getPublicUsers, getPublicOrgs,
  createPublicOrg, updatePublicOrg, deletePublicOrg, assignUserToPublicOrg,
  invitePublicOrgUser, getPublicOrgUsers, resendPublicOrgInvite, overridePublicUserPlan,
  getSuperAdmins, addSuperAdmin, removeSuperAdmin,
  getPublicSuperAdmins, addPublicSuperAdmin, removePublicSuperAdmin,
  AssessmentResult, AdminUser, Org, OrgUser, PublicUser, PublicOrg, SuperAdmin,
} from '../api/admin';
import { getUserEmail, isSuperAdmin } from '../auth/cognito';

// ─── shared primitives ───────────────────────────────────────────────────────

const card: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 16, padding: '20px 24px',
};

function pill(color: string, text: string) {
  return (
    <span style={{
      background: `${color}20`, border: `1px solid ${color}40`,
      color, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 100,
    }}>{text}</span>
  );
}

function inp(value: string, onChange: (v: string) => void, opts?: { placeholder?: string; type?: string; required?: boolean }) {
  return (
    <input
      type={opts?.type || 'text'} value={value} onChange={e => onChange(e.target.value)}
      placeholder={opts?.placeholder} required={opts?.required}
      style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 13, outline: 'none', fontFamily: 'inherit' }}
    />
  );
}

function sel(value: string, onChange: (v: string) => void, options: { value: string; label: string }[]) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      style={{ width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 13, outline: 'none' }}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

const SPIN = `@keyframes spin { to { transform: rotate(360deg); } }`;

function Spinner({ color = '#818cf8' }: { color?: string }) {
  return (
    <>
      <style>{SPIN}</style>
      <div style={{ width: 32, height: 32, border: `3px solid rgba(255,255,255,0.08)`, borderTopColor: color, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
    </>
  );
}

function StatusMsg({ status }: { status: { type: 'success' | 'error'; msg: string } | null }) {
  if (!status) return null;
  return (
    <div style={{ padding: '10px 14px', borderRadius: 8, fontSize: 13, background: status.type === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${status.type === 'success' ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`, color: status.type === 'success' ? '#22c55e' : '#ef4444' }}>
      {status.msg}
    </div>
  );
}

// ─── org wizard ──────────────────────────────────────────────────────────────

const PLAN_OPTS = [
  { value: 'starter',      label: 'Starter' },
  { value: 'professional', label: 'Professional' },
  { value: 'enterprise',   label: 'Enterprise' },
];

const STATUS_OPTS = [
  { value: 'trial',     label: 'Trial' },
  { value: 'active',    label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
];

interface WizardData {
  name: string; domain: string; plan: string; seats: number; billingEmail: string;
  adminEmail: string; adminFirst: string; adminLast: string;
}

function OrgWizard({ mode, accentColor, onClose, onCreated }: {
  mode: 'enterprise' | 'public';
  accentColor: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [data, setData] = useState<WizardData>({ name: '', domain: '', plan: 'starter', seats: mode === 'enterprise' ? 10 : 25, billingEmail: '', adminEmail: '', adminFirst: '', adminLast: '' });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [created, setCreated] = useState<{ orgId: string; joinCode?: string } | null>(null);

  function set(key: keyof WizardData, val: string | number) {
    setData(d => ({ ...d, [key]: val }));
  }

  async function handleCreate() {
    setLoading(true);
    setStatus(null);
    const orgPayload = { name: data.name, domain: data.domain, plan: data.plan, seats: data.seats, billingEmail: data.billingEmail };
    const res = mode === 'enterprise'
      ? await createOrg(orgPayload)
      : await createPublicOrg(orgPayload);

    if (!res.success || !res.orgId) {
      setStatus({ type: 'error', msg: res.error || 'Failed to create organisation.' });
      setLoading(false);
      return;
    }

    const orgId = res.orgId;
    const joinCode = (res as any).joinCode as string | undefined;
    setCreated({ orgId, joinCode });

    if (data.adminEmail.trim()) {
      const invRes = mode === 'enterprise'
        ? await inviteUser(data.adminEmail.trim(), data.adminFirst.trim(), data.adminLast.trim(), orgId, 'org_admin')
        : await invitePublicOrgUser(orgId, data.adminEmail.trim(), data.adminFirst.trim(), data.adminLast.trim(), 'org_admin');
      if (!invRes.success) {
        setStatus({ type: 'error', msg: `Org created (${orgId}) but failed to send invite: ${invRes.error}` });
      } else {
        setStatus({ type: 'success', msg: `Organisation created and invite sent to ${data.adminEmail}.` });
      }
    } else {
      setStatus({ type: 'success', msg: `Organisation created.` });
    }
    setLoading(false);
  }

  const stepLabel = ['Organisation details', 'Assign Org Admin', 'Review & confirm'];

  const overlay: React.CSSProperties = { position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 20 };
  const modal: React.CSSProperties = { background: '#0d1b3e', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 20, padding: '36px 40px', maxWidth: 520, width: '100%', maxHeight: '90vh', overflowY: 'auto' as const };
  const labelStyle: React.CSSProperties = { fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 6, fontWeight: 600, display: 'block' };

  if (created && status?.type === 'success') {
    return (
      <div style={overlay} onClick={e => e.target === e.currentTarget && (onCreated(), onClose())}>
        <div style={modal}>
          <div style={{ textAlign: 'center', padding: '8px 0 24px' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>✓</div>
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, color: accentColor }}>Organisation Created</div>
            {data.adminEmail && <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 20 }}>Invite sent to {data.adminEmail}</div>}
            {created.joinCode && (
              <div style={{ ...card, marginBottom: 20, textAlign: 'left' }}>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 6, fontWeight: 600 }}>JOIN CODE</div>
                <div style={{ fontFamily: 'monospace', fontSize: 22, fontWeight: 800, letterSpacing: '0.15em', color: accentColor }}>{created.joinCode}</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 4 }}>Share this with users to join the organisation</div>
              </div>
            )}
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '10px 14px', marginBottom: 24 }}>
              The Org Admin will receive a branded email with a temporary password. On first login they will be prompted to complete payment before accessing their portal.
            </div>
            <button onClick={() => { onCreated(); onClose(); }} style={{ background: `linear-gradient(135deg,${accentColor},${accentColor}cc)`, border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 700, padding: '12px 28px', cursor: 'pointer' }}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modal}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>New Organisation</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>Step {step} of 3 — {stepLabel[step - 1]}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>

        {/* Step indicators */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 28 }}>
          {[1, 2, 3].map(s => (
            <div key={s} style={{ height: 3, flex: 1, borderRadius: 2, background: s <= step ? accentColor : 'rgba(255,255,255,0.1)' }} />
          ))}
        </div>

        {/* Step 1 */}
        {step === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div><label style={labelStyle}>Organisation name *</label>{inp(data.name, v => set('name', v), { placeholder: 'Acme Corp', required: true })}</div>
            <div><label style={labelStyle}>{mode === 'public' ? 'Email domain (for auto-assign)' : 'Domain *'}</label>{inp(data.domain, v => set('domain', v), { placeholder: 'acme.com', required: mode === 'enterprise' })}</div>
            <div><label style={labelStyle}>Billing email</label>{inp(data.billingEmail, v => set('billingEmail', v), { type: 'email', placeholder: 'billing@acme.com' })}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><label style={labelStyle}>Plan</label>{sel(data.plan, v => set('plan', v), PLAN_OPTS)}</div>
              <div>
                <label style={labelStyle}>Seats</label>
                <input type="number" value={data.seats} onChange={e => set('seats', Number(e.target.value))} min={1}
                  style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 13, outline: 'none', fontFamily: 'inherit' }} />
              </div>
            </div>
            <button disabled={!data.name || (mode === 'enterprise' && !data.domain)} onClick={() => setStep(2)}
              style={{ background: `linear-gradient(135deg,${accentColor},${accentColor}cc)`, border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 700, padding: '12px', cursor: 'pointer', opacity: (!data.name || (mode === 'enterprise' && !data.domain)) ? 0.5 : 1 }}>
              Next →
            </button>
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', marginBottom: 4, lineHeight: 1.6 }}>
              Assign an Org Admin who will manage this organisation. They will receive a branded invite email and be prompted to complete payment on first login.
              <br /><span style={{ color: 'rgba(255,255,255,0.25)' }}>You can skip this and add an Org Admin later from the organisation detail view.</span>
            </div>
            <div><label style={labelStyle}>Admin email</label>{inp(data.adminEmail, v => set('adminEmail', v), { type: 'email', placeholder: 'admin@acme.com' })}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><label style={labelStyle}>First name</label>{inp(data.adminFirst, v => set('adminFirst', v), { placeholder: 'Jane' })}</div>
              <div><label style={labelStyle}>Last name</label>{inp(data.adminLast, v => set('adminLast', v), { placeholder: 'Smith' })}</div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setStep(1)} style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, color: 'rgba(255,255,255,0.6)', fontSize: 14, fontWeight: 600, padding: '12px', cursor: 'pointer' }}>← Back</button>
              <button onClick={() => setStep(3)} style={{ flex: 2, background: `linear-gradient(135deg,${accentColor},${accentColor}cc)`, border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 700, padding: '12px', cursor: 'pointer' }}>
                {data.adminEmail ? 'Next →' : 'Skip →'}
              </button>
            </div>
          </div>
        )}

        {/* Step 3 */}
        {step === 3 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ ...card }}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 14 }}>Organisation</div>
              {[
                ['Name', data.name], ['Domain', data.domain || '—'], ['Plan', data.plan], ['Seats', String(data.seats)], ['Billing', data.billingEmail || '—'],
              ].map(([l, v]) => (
                <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.06)', fontSize: 13 }}>
                  <span style={{ color: 'rgba(255,255,255,0.4)' }}>{l}</span>
                  <span style={{ fontWeight: 600 }}>{v}</span>
                </div>
              ))}
            </div>
            {data.adminEmail && (
              <div style={{ ...card }}>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 14 }}>Org Admin Invite</div>
                {[
                  ['Email', data.adminEmail],
                  ['Name', [data.adminFirst, data.adminLast].filter(Boolean).join(' ') || '—'],
                  ['Role', 'Org Admin'],
                ].map(([l, v]) => (
                  <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.06)', fontSize: 13 }}>
                    <span style={{ color: 'rgba(255,255,255,0.4)' }}>{l}</span>
                    <span style={{ fontWeight: 600 }}>{v}</span>
                  </div>
                ))}
              </div>
            )}
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.03)', borderRadius: 8, padding: '10px 14px', lineHeight: 1.6 }}>
              {data.adminEmail
                ? `A Stripe subscription will be created and ${data.adminEmail} will receive an invite. On first login they will complete payment for ${data.seats} seat${data.seats !== 1 ? 's' : ''} on the ${data.plan} plan.`
                : `A Stripe subscription will be created for ${data.seats} seat${data.seats !== 1 ? 's' : ''} on the ${data.plan} plan. You can assign an Org Admin later.`}
            </div>
            <StatusMsg status={status} />
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setStep(2)} disabled={loading} style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, color: 'rgba(255,255,255,0.6)', fontSize: 14, fontWeight: 600, padding: '12px', cursor: 'pointer' }}>← Back</button>
              <button onClick={handleCreate} disabled={loading} style={{ flex: 2, background: loading ? 'rgba(99,102,241,0.3)' : `linear-gradient(135deg,${accentColor},${accentColor}cc)`, border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 700, padding: '12px', cursor: loading ? 'not-allowed' : 'pointer' }}>
                {loading ? 'Creating…' : 'Create Organisation'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

type Platform = 'enterprise' | 'public';
type EntTab   = 'overview' | 'orgs' | 'users' | 'assessments' | 'super_admins';
type PubTab   = 'orgs' | 'users' | 'super_admins';

interface Props { visiblePlatforms: 'both' | 'enterprise' | 'public'; }

export default function FullAdminDashboard({ visiblePlatforms }: Props) {
  const myEmail   = getUserEmail();
  const superAdm  = isSuperAdmin();
  const showBoth  = visiblePlatforms === 'both';

  const [platform, setPlatform] = useState<Platform>(visiblePlatforms === 'public' ? 'public' : 'enterprise');
  const [entTab, setEntTab]     = useState<EntTab>('overview');
  const [pubTab, setPubTab]     = useState<PubTab>('orgs');

  // Enterprise data
  const [results, setResults]         = useState<AssessmentResult[]>([]);
  const [users, setUsers]             = useState<AdminUser[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);

  // Enterprise orgs
  const [orgs, setOrgs]                 = useState<Org[]>([]);
  const [orgsLoading, setOrgsLoading]   = useState(false);
  const [selectedOrg, setSelectedOrg]   = useState<Org | null>(null);
  const [orgUsers, setOrgUsers]         = useState<OrgUser[]>([]);
  const [orgUsersLoading, setOrgUsersLoading] = useState(false);
  const [showOrgWizard, setShowOrgWizard] = useState(false);
  const [editLicense, setEditLicense]   = useState<{ plan: string; seats: number; status: string } | null>(null);
  const [licenseStatus, setLicenseStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  // Enterprise super admins
  const [entSuperAdmins, setEntSuperAdmins]       = useState<SuperAdmin[]>([]);
  const [entSuperAdminsLoading, setEntSuperAdminsLoading] = useState(false);
  const [newEntAdminEmail, setNewEntAdminEmail]   = useState('');
  const [addEntAdminStatus, setAddEntAdminStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [addEntAdminLoading, setAddEntAdminLoading] = useState(false);

  // Enterprise invite (inside org detail)
  const [showInviteModal, setShowInviteModal]   = useState(false);
  const [inviteEmail, setInviteEmail]           = useState('');
  const [inviteFirst, setInviteFirst]           = useState('');
  const [inviteLast, setInviteLast]             = useState('');
  const [inviteRole, setInviteRole]             = useState('user');
  const [inviteLoading, setInviteLoading]       = useState(false);
  const [inviteStatus, setInviteStatus]         = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  // Public data
  const [publicUsers, setPublicUsers]           = useState<PublicUser[]>([]);
  const [publicLoading, setPublicLoading]       = useState(false);
  const [publicOrgs, setPublicOrgs]             = useState<PublicOrg[]>([]);
  const [publicOrgsLoading, setPublicOrgsLoading] = useState(false);
  const [selectedPublicOrg, setSelectedPublicOrg] = useState<PublicOrg | null>(null);
  const [pubOrgUsers, setPubOrgUsers]           = useState<OrgUser[]>([]);
  const [pubOrgUsersLoading, setPubOrgUsersLoading] = useState(false);
  const [showPublicOrgWizard, setShowPublicOrgWizard] = useState(false);
  const [resendStatus, setResendStatus]         = useState<{ userId: string; type: 'success' | 'error'; msg: string } | null>(null);
  const [assigningUser, setAssigningUser]       = useState<string | null>(null);
  const [assignOrgId, setAssignOrgId]           = useState('');
  const [assignRole, setAssignRole]             = useState('user');
  const [assignStatus, setAssignStatus]         = useState<{ userId: string; type: 'success' | 'error'; msg: string } | null>(null);
  const [overridingPlanUser, setOverridingPlanUser] = useState<string | null>(null);
  const [overridePlanValue, setOverridePlanValue]   = useState<'paid' | 'free'>('paid');
  const [overridePlanStatus, setOverridePlanStatus] = useState<{ userId: string; type: 'success' | 'error'; msg: string } | null>(null);

  // Public org invite
  const [showPubOrgInvite, setShowPubOrgInvite] = useState(false);
  const [pubInviteEmail, setPubInviteEmail]     = useState('');
  const [pubInviteFirst, setPubInviteFirst]     = useState('');
  const [pubInviteLast, setPubInviteLast]       = useState('');
  const [pubInviteRole, setPubInviteRole]       = useState('user');
  const [pubInviteLoading, setPubInviteLoading] = useState(false);
  const [pubInviteStatus, setPubInviteStatus]   = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  // Public super admins
  const [pubSuperAdmins, setPubSuperAdmins]         = useState<SuperAdmin[]>([]);
  const [pubSuperAdminsLoading, setPubSuperAdminsLoading] = useState(false);
  const [newPubAdminEmail, setNewPubAdminEmail]     = useState('');
  const [addPubAdminStatus, setAddPubAdminStatus]   = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [addPubAdminLoading, setAddPubAdminLoading] = useState(false);

  useEffect(() => {
    if (visiblePlatforms !== 'public') {
      Promise.all([getAllResults(), getAllUsers()]).then(([r, u]) => {
        setResults(r); setUsers(u); setDataLoading(false);
      });
    } else {
      setDataLoading(false);
    }
  }, [visiblePlatforms]);

  useEffect(() => {
    if (platform === 'enterprise' && entTab === 'orgs' && orgs.length === 0) loadOrgs();
    if (platform === 'enterprise' && entTab === 'super_admins' && entSuperAdmins.length === 0) loadEntSuperAdmins();
    if (platform === 'public' && pubTab === 'orgs' && publicOrgs.length === 0) loadPublicOrgs();
    if (platform === 'public' && pubTab === 'users' && publicUsers.length === 0) loadPublicUsers();
    if (platform === 'public' && pubTab === 'super_admins' && pubSuperAdmins.length === 0) loadPubSuperAdmins();
  }, [platform, entTab, pubTab]); // eslint-disable-line

  function loadOrgs() { setOrgsLoading(true); getOrgs().then(o => { setOrgs(o); setOrgsLoading(false); }); }
  function loadPublicOrgs() { setPublicOrgsLoading(true); getPublicOrgs().then(o => { setPublicOrgs(o); setPublicOrgsLoading(false); }); }
  function loadPublicUsers() { setPublicLoading(true); getPublicUsers().then(u => { setPublicUsers(u); setPublicLoading(false); }); }
  function loadEntSuperAdmins() { setEntSuperAdminsLoading(true); getSuperAdmins().then(a => { setEntSuperAdmins(a); setEntSuperAdminsLoading(false); }); }
  function loadPubSuperAdmins() { setPubSuperAdminsLoading(true); getPublicSuperAdmins().then(a => { setPubSuperAdmins(a); setPubSuperAdminsLoading(false); }); }

  async function selectOrg(org: Org) {
    setSelectedOrg(org);
    setEditLicense({ plan: org.plan, seats: org.seats, status: org.status });
    setLicenseStatus(null); setShowInviteModal(false); setInviteStatus(null);
    setOrgUsersLoading(true);
    setOrgUsers(await getOrgUsers(org.orgId));
    setOrgUsersLoading(false);
  }

  async function handleSaveLicense() {
    if (!selectedOrg || !editLicense) return;
    setLicenseStatus(null);
    const res = await updateOrg(selectedOrg.orgId, editLicense);
    if (res.success) {
      setLicenseStatus({ type: 'success', msg: 'License updated.' });
      setSelectedOrg({ ...selectedOrg, ...editLicense });
      setOrgs(orgs.map(o => o.orgId === selectedOrg.orgId ? { ...o, ...editLicense } : o));
    } else {
      setLicenseStatus({ type: 'error', msg: res.error || 'Update failed.' });
    }
  }

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedOrg) return;
    setInviteLoading(true); setInviteStatus(null);
    const res = await inviteUser(inviteEmail.trim(), inviteFirst.trim(), inviteLast.trim(), selectedOrg.orgId, inviteRole);
    setInviteLoading(false);
    if (res.success) {
      setInviteStatus({ type: 'success', msg: `Invite sent to ${inviteEmail}` });
      setInviteEmail(''); setInviteFirst(''); setInviteLast(''); setInviteRole('user');
      setSelectedOrg(o => o ? { ...o, seatsUsed: o.seatsUsed + 1 } : o);
      setOrgs(prev => prev.map(o => o.orgId === selectedOrg.orgId ? { ...o, seatsUsed: o.seatsUsed + 1 } : o));
    } else {
      setInviteStatus({ type: 'error', msg: res.error || 'Failed to send invite.' });
    }
  }

  const completed    = getCompletedResults(results);
  const avgScore     = getAvgScore(results);
  const gapFreq: Record<string, number> = {};
  completed.forEach(r => {
    const key = `${r.assessmentType === 'tabletop' ? 'Tabletop' : 'Engineering'} — ${r.moduleId}`;
    gapFreq[key] = (gapFreq[key] || 0) + 1;
  });
  const topGaps = Object.entries(gapFreq).sort((a, b) => b[1] - a[1]).slice(0, 5);

  // ── tab button helpers ────────────────────────────────────────────────────

  function platformTab(p: Platform, label: string, color: string) {
    const active = platform === p;
    return (
      <button onClick={() => setPlatform(p)} style={{
        background: active ? `${color}25` : 'rgba(255,255,255,0.04)',
        border: `1px solid ${active ? `${color}60` : 'rgba(255,255,255,0.08)'}`,
        color: active ? color : 'rgba(255,255,255,0.45)',
        padding: '10px 24px', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer',
      }}>{label}</button>
    );
  }

  function entTabBtn(key: EntTab, label: string) {
    const active = entTab === key;
    return (
      <button onClick={() => { setEntTab(key); setSelectedUser(null); }} style={{
        background: active ? 'rgba(99,102,241,0.18)' : 'transparent',
        border: `1px solid ${active ? 'rgba(99,102,241,0.45)' : 'rgba(255,255,255,0.08)'}`,
        color: active ? '#a5b4fc' : 'rgba(255,255,255,0.45)',
        padding: '7px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
      }}>{label}</button>
    );
  }

  function pubTabBtn(key: PubTab, label: string) {
    const active = pubTab === key;
    return (
      <button onClick={() => setPubTab(key)} style={{
        background: active ? 'rgba(16,185,129,0.18)' : 'transparent',
        border: `1px solid ${active ? 'rgba(16,185,129,0.45)' : 'rgba(255,255,255,0.08)'}`,
        color: active ? '#6ee7b7' : 'rgba(255,255,255,0.45)',
        padding: '7px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
      }}>{label}</button>
    );
  }

  if (dataLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spinner />
        <p style={{ color: 'rgba(255,255,255,0.35)', marginTop: 16, fontSize: 13 }}>Loading platform data…</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 24px' }}>

      {/* Wizards */}
      {showOrgWizard && (
        <OrgWizard mode="enterprise" accentColor="#6366f1" onClose={() => setShowOrgWizard(false)} onCreated={loadOrgs} />
      )}
      {showPublicOrgWizard && (
        <OrgWizard mode="public" accentColor="#10b981" onClose={() => setShowPublicOrgWizard(false)} onCreated={loadPublicOrgs} />
      )}

      {/* Platform switcher */}
      {showBoth && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 28 }}>
          {platformTab('enterprise', '🏢 Enterprise Platform', '#818cf8')}
          {platformTab('public',     '🌐 Public Platform',     '#34d399')}
        </div>
      )}

      {/* ══════ ENTERPRISE ══════ */}
      {platform === 'enterprise' && (
        <>
          {/* Sub-tabs */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' as const, marginBottom: 28 }}>
            {entTabBtn('overview',     'Overview')}
            {entTabBtn('orgs',         `Orgs${orgs.length ? ` (${orgs.length})` : ''}`)}
            {entTabBtn('users',        `Users${users.length ? ` (${users.length})` : ''}`)}
            {entTabBtn('assessments',  `Assessments${completed.length ? ` (${completed.length})` : ''}`)}
            {entTabBtn('super_admins', 'Super Admins')}
          </div>

          {/* OVERVIEW */}
          {entTab === 'overview' && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 14, marginBottom: 28 }}>
                {[
                  { label: 'Total users',            value: users.length,    color: '#818cf8' },
                  { label: 'Completed assessments',  value: completed.length, color: '#818cf8' },
                  { label: 'Platform avg score',     value: avgScore || '—', color: avgScore ? getRisk(avgScore).color : '#818cf8' },
                  { label: 'Total attempts',         value: results.length,  color: '#818cf8' },
                ].map((s, i) => (
                  <div key={i} style={card}>
                    <div style={{ fontSize: 30, fontWeight: 700, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>{s.label}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <div style={card}>
                  <div style={{ fontSize: 11, textTransform: 'uppercase' as const, letterSpacing: 2, color: '#818cf8', marginBottom: 16, fontWeight: 700 }}>Score distribution</div>
                  {[['Low Risk (≥80)', '#22c55e', completed.filter(r => r.score >= 80).length],
                    ['Medium Risk (50–79)', '#f97316', completed.filter(r => r.score >= 50 && r.score < 80).length],
                    ['High Risk (<50)', '#ef4444', completed.filter(r => r.score < 50).length],
                  ].map(([label, color, count]) => (
                    <div key={label as string} style={{ marginBottom: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)' }}>{label}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: color as string }}>{count}</span>
                      </div>
                      <div style={{ height: 6, background: 'rgba(255,255,255,0.07)', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ width: completed.length > 0 ? `${((count as number) / completed.length) * 100}%` : '0%', height: '100%', background: color as string, borderRadius: 4 }} />
                      </div>
                    </div>
                  ))}
                </div>
                <div style={card}>
                  <div style={{ fontSize: 11, textTransform: 'uppercase' as const, letterSpacing: 2, color: '#818cf8', marginBottom: 16, fontWeight: 700 }}>Assessment breakdown</div>
                  {[['tabletop', 'Privacy Tabletop'], ['engineering', 'Privacy Engineering']].map(([type, label]) => {
                    const cnt = completed.filter(r => r.assessmentType === type).length;
                    const avg = cnt > 0 ? Math.round(completed.filter(r => r.assessmentType === type).reduce((a, b) => a + b.score, 0) / cnt) : 0;
                    return (
                      <div key={type} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)' }}>{label}</span>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>{cnt} completed</span>
                          {cnt > 0 && <span style={{ fontSize: 13, fontWeight: 700, color: getRisk(avg).color }}>{avg} avg</span>}
                        </div>
                      </div>
                    );
                  })}
                  {topGaps.length > 0 && (
                    <>
                      <div style={{ fontSize: 11, textTransform: 'uppercase' as const, letterSpacing: 2, color: '#818cf8', margin: '20px 0 12px', fontWeight: 700 }}>Most attempted modules</div>
                      {topGaps.map(([gap, count]) => (
                        <div key={gap} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>{gap}</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#f97316', background: 'rgba(249,115,22,0.12)', padding: '2px 8px', borderRadius: 6 }}>{count}x</span>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ORGS LIST */}
          {entTab === 'orgs' && !selectedOrg && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div style={{ fontSize: 17, fontWeight: 700 }}>
                  Organisations {orgs.length > 0 && <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>({orgs.length})</span>}
                </div>
                <button onClick={() => setShowOrgWizard(true)} style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', border: 'none', borderRadius: 10, color: '#fff', fontSize: 13, fontWeight: 700, padding: '9px 18px', cursor: 'pointer' }}>
                  + New Organisation
                </button>
              </div>
              {orgsLoading ? <div style={{ textAlign: 'center', padding: 48 }}><Spinner /></div>
                : orgs.length === 0 ? <div style={{ ...card, textAlign: 'center', padding: 48, color: 'rgba(255,255,255,0.35)' }}>No organisations yet.</div>
                : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {orgs.map(org => {
                      const planColor   = org.plan === 'enterprise' ? '#a78bfa' : org.plan === 'professional' ? '#60a5fa' : '#34d399';
                      const statusColor = org.status === 'active' ? '#22c55e' : org.status === 'trial' ? '#f97316' : '#ef4444';
                      const seatsPct    = org.seats === -1 ? 0 : (org.seatsUsed / org.seats) * 100;
                      return (
                        <div key={org.orgId} onClick={() => selectOrg(org)} style={{ ...card, cursor: 'pointer', padding: '16px 22px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' as const, gap: 12 }}>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 2 }}>{org.name}</div>
                              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>{org.domain}</div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' as const }}>
                              {pill(planColor, org.plan.charAt(0).toUpperCase() + org.plan.slice(1))}
                              {pill(statusColor, org.status.charAt(0).toUpperCase() + org.status.slice(1))}
                              <div style={{ textAlign: 'center', minWidth: 70 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#818cf8' }}>
                                  {org.seats === -1 ? `${org.seatsUsed} / ∞` : `${org.seatsUsed} / ${org.seats}`}
                                </div>
                                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginBottom: 3 }}>seats</div>
                                {org.seats !== -1 && (
                                  <div style={{ height: 4, width: 70, background: 'rgba(255,255,255,0.08)', borderRadius: 2 }}>
                                    <div style={{ height: '100%', width: `${seatsPct}%`, background: seatsPct > 90 ? '#ef4444' : '#818cf8', borderRadius: 2 }} />
                                  </div>
                                )}
                              </div>
                              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>{new Date(org.createdAt).toLocaleDateString()}</div>
                              <div style={{ fontSize: 12, color: '#818cf8' }}>View →</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
            </div>
          )}

          {/* ORG DETAIL */}
          {entTab === 'orgs' && selectedOrg && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <button onClick={() => { setSelectedOrg(null); setOrgUsers([]); }} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', padding: '8px 16px', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
                  ← Back to organisations
                </button>
                {superAdm && (
                  <button onClick={async () => {
                    if (!window.confirm(`Permanently delete "${selectedOrg.name}" and all users? This cannot be undone.`)) return;
                    const res = await deleteOrg(selectedOrg.orgId);
                    if (res.success) { setOrgs(prev => prev.filter(o => o.orgId !== selectedOrg.orgId)); setSelectedOrg(null); setOrgUsers([]); }
                  }} style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.4)', color: '#f87171', padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                    Delete Organisation
                  </button>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 20, marginBottom: 20 }}>
                <div style={card}>
                  <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 3 }}>{selectedOrg.name}</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 16 }}>{selectedOrg.domain}</div>
                  {[
                    ['Plan',      selectedOrg.plan.charAt(0).toUpperCase() + selectedOrg.plan.slice(1)],
                    ['Status',    selectedOrg.status.charAt(0).toUpperCase() + selectedOrg.status.slice(1)],
                    ['Seats',     selectedOrg.seats === -1 ? `${selectedOrg.seatsUsed} (∞)` : `${selectedOrg.seatsUsed} / ${selectedOrg.seats}`],
                    ['Billing',   selectedOrg.billingEmail || '—'],
                    ['Created',   new Date(selectedOrg.createdAt).toLocaleDateString()],
                    ...(selectedOrg.trialEndsAt ? [['Trial ends', new Date(selectedOrg.trialEndsAt).toLocaleDateString()]] : []),
                  ].map(([l, v]) => (
                    <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.05)', fontSize: 13 }}>
                      <span style={{ color: 'rgba(255,255,255,0.4)' }}>{l}</span>
                      <span style={{ fontWeight: 600 }}>{v}</span>
                    </div>
                  ))}
                </div>
                <div style={card}>
                  <div style={{ fontSize: 11, textTransform: 'uppercase' as const, letterSpacing: 2, color: '#818cf8', marginBottom: 18, fontWeight: 700 }}>License Management</div>
                  {editLicense && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      <div>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 6, fontWeight: 600 }}>Plan</div>
                        {sel(editLicense.plan, v => setEditLicense(p => ({ ...p!, plan: v })), PLAN_OPTS)}
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 6, fontWeight: 600 }}>Seats (-1 = unlimited)</div>
                        <input type="number" value={editLicense.seats} onChange={e => setEditLicense(p => ({ ...p!, seats: Number(e.target.value) }))} min={-1}
                          style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 13, outline: 'none', fontFamily: 'inherit' }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 6, fontWeight: 600 }}>Status</div>
                        {sel(editLicense.status, v => setEditLicense(p => ({ ...p!, status: v })), STATUS_OPTS)}
                      </div>
                      <StatusMsg status={licenseStatus} />
                      <button onClick={handleSaveLicense} style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 700, padding: '10px 20px', cursor: 'pointer', alignSelf: 'flex-start' }}>
                        Save Changes
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Members */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase' as const, letterSpacing: 2, color: '#818cf8', fontWeight: 700 }}>
                  Members {orgUsers.length > 0 && `(${orgUsers.length})`}
                </div>
                <button onClick={() => { setShowInviteModal(v => !v); setInviteStatus(null); }} style={{ background: showInviteModal ? 'rgba(255,255,255,0.06)' : 'rgba(99,102,241,0.15)', border: `1px solid ${showInviteModal ? 'rgba(255,255,255,0.12)' : 'rgba(99,102,241,0.4)'}`, color: showInviteModal ? 'rgba(255,255,255,0.5)' : '#a5b4fc', fontSize: 12, fontWeight: 700, padding: '6px 14px', borderRadius: 8, cursor: 'pointer' }}>
                  {showInviteModal ? 'Cancel' : '+ Invite Member'}
                </button>
              </div>

              {showInviteModal && (
                <div style={{ ...card, marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#a5b4fc', marginBottom: 14 }}>
                    Invite to {selectedOrg.name}
                    {selectedOrg.seats !== -1 && <span style={{ fontWeight: 400, fontSize: 12, color: 'rgba(255,255,255,0.35)', marginLeft: 8 }}>{selectedOrg.seats - selectedOrg.seatsUsed} seats available</span>}
                  </div>
                  {selectedOrg.seats !== -1 && selectedOrg.seatsUsed >= selectedOrg.seats ? (
                    <div style={{ padding: '12px 16px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', fontSize: 13 }}>
                      No seats available. Update the seat limit before inviting more users.
                    </div>
                  ) : (
                    <form onSubmit={handleInvite} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      {[
                        { label: 'Email *', value: inviteEmail, setter: setInviteEmail, type: 'email', placeholder: 'user@company.com', required: true },
                        { label: 'First name', value: inviteFirst, setter: setInviteFirst, type: 'text', placeholder: 'Jane', required: false },
                        { label: 'Last name', value: inviteLast, setter: setInviteLast, type: 'text', placeholder: 'Smith', required: false },
                      ].map(({ label, value, setter, type, placeholder, required }) => (
                        <div key={label}>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 5, fontWeight: 600 }}>{label}</div>
                          <input type={type} value={value} onChange={e => setter(e.target.value)} placeholder={placeholder} required={required}
                            style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 13, outline: 'none', fontFamily: 'inherit' }} />
                        </div>
                      ))}
                      <div>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 5, fontWeight: 600 }}>Role</div>
                        {sel(inviteRole, setInviteRole, [{ value: 'user', label: 'User' }, { value: 'org_admin', label: 'Org Admin' }])}
                      </div>
                      <div style={{ gridColumn: '1/-1' }}>
                        <StatusMsg status={inviteStatus} />
                        <button type="submit" disabled={inviteLoading} style={{ background: inviteLoading ? 'rgba(99,102,241,0.3)' : 'linear-gradient(135deg,#6366f1,#8b5cf6)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 700, padding: '10px 24px', cursor: inviteLoading ? 'not-allowed' : 'pointer', marginTop: inviteStatus ? 10 : 0 }}>
                          {inviteLoading ? 'Sending…' : 'Send Invite'}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}

              {orgUsersLoading ? <div style={{ ...card, textAlign: 'center', padding: 32 }}><Spinner /></div>
                : orgUsers.length === 0 ? <div style={{ ...card, color: 'rgba(255,255,255,0.35)', padding: 32, textAlign: 'center' }}>No members yet.</div>
                : (
                  <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                          {['Name', 'Email', 'Role', 'Status', 'Invited', ''].map(h => (
                            <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, textTransform: 'uppercase' as const, letterSpacing: 1, color: 'rgba(255,255,255,0.35)', fontWeight: 600 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {orgUsers.map((u, i) => {
                          const name        = [u.firstName, u.lastName].filter(Boolean).join(' ') || '—';
                          const roleColor   = u.role === 'super_admin' ? '#f59e0b' : u.role === 'org_admin' ? '#a78bfa' : '#60a5fa';
                          const statusColor = u.status === 'active' ? '#22c55e' : '#ef4444';
                          return (
                            <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                              <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600 }}>{name}</td>
                              <td style={{ padding: '12px 16px', fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>{u.email}</td>
                              <td style={{ padding: '12px 16px' }}>{pill(roleColor, u.role.replace('_', ' '))}</td>
                              <td style={{ padding: '12px 16px' }}>{pill(statusColor, u.status)}</td>
                              <td style={{ padding: '12px 16px', fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>{new Date(u.invitedAt).toLocaleDateString()}</td>
                              <td style={{ padding: '12px 16px' }}>
                                <div style={{ display: 'flex', gap: 6 }}>
                                  {u.status === 'active' && u.role !== 'super_admin' && (
                                    <button onClick={async () => { const res = await removeOrgUser(u.userId); if (res.success) { setOrgUsers(prev => prev.map(x => x.userId === u.userId ? { ...x, status: 'suspended' } : x)); setSelectedOrg(o => o ? { ...o, seatsUsed: Math.max(0, o.seatsUsed - 1) } : o); } }}
                                      style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 6, cursor: 'pointer' }}>
                                      Suspend
                                    </button>
                                  )}
                                  {superAdm && u.role !== 'super_admin' && (
                                    <button onClick={async () => { if (!window.confirm(`Permanently delete ${u.email}?`)) return; const res = await deleteEnterpriseUser(u.userId); if (res.success) setOrgUsers(prev => prev.filter(x => x.userId !== u.userId)); }}
                                      style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.5)', color: '#f87171', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 6, cursor: 'pointer' }}>
                                      Delete
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
            </div>
          )}

          {/* USERS */}
          {entTab === 'users' && !selectedUser && (
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 20 }}>All users</div>
              {users.length === 0 ? <div style={{ ...card, textAlign: 'center', padding: 48, color: 'rgba(255,255,255,0.35)' }}>No users found.</div>
                : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {users.map(u => {
                      const uc   = u.assessments.filter((a: AssessmentResult) => a.isComplete);
                      const uavg = uc.length > 0 ? Math.round(uc.reduce((a: number, b: AssessmentResult) => a + b.score, 0) / uc.length) : 0;
                      const risk = getRisk(uavg);
                      return (
                        <div key={u.userId} onClick={() => setSelectedUser(u.userId)} style={{ ...card, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' as const, gap: 12, padding: '14px 20px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                            <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#a855f7)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                              {u.userId.slice(0, 2).toUpperCase()}
                            </div>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 700, fontFamily: 'monospace', color: '#818cf8' }}>{u.userId.slice(0, 12)}…</div>
                              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>Last active: {new Date(u.lastActive).toLocaleDateString()}</div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: 20, fontWeight: 700, color: uavg > 0 ? risk.color : 'rgba(255,255,255,0.3)' }}>{uavg || '—'}</div>
                              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>Avg</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: 20, fontWeight: 700, color: '#818cf8' }}>{uc.length}</div>
                              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>Done</div>
                            </div>
                            {uavg > 0 && pill(risk.color, risk.label + ' Risk')}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
            </div>
          )}

          {/* USER DETAIL */}
          {entTab === 'users' && selectedUser && (() => {
            const ud  = users.find(u => u.userId === selectedUser);
            const ur  = results.filter(r => r.userId === selectedUser && r.isComplete);
            if (!ud) return null;
            return (
              <div>
                <button onClick={() => setSelectedUser(null)} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', padding: '8px 16px', borderRadius: 8, fontSize: 13, cursor: 'pointer', marginBottom: 24 }}>← Back</button>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 20 }}>
                  <div style={card}>
                    <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#a855f7)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
                      {ud.userId.slice(0, 2).toUpperCase()}
                    </div>
                    <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#818cf8', wordBreak: 'break-all' as const, marginBottom: 4 }}>{ud.userId}</div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginBottom: 20 }}>Last active: {new Date(ud.lastActive).toLocaleDateString()}</div>
                    {[['Total attempts', ud.assessments.length], ['Completed', ur.length]].map(([l, v]) => (
                      <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)', fontSize: 13 }}>
                        <span style={{ color: 'rgba(255,255,255,0.4)' }}>{l}</span><span style={{ fontWeight: 600 }}>{v}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div style={{ fontSize: 11, textTransform: 'uppercase' as const, letterSpacing: 2, color: '#818cf8', marginBottom: 14, fontWeight: 700 }}>Completed assessments</div>
                    {ur.length === 0 ? <div style={{ ...card, color: 'rgba(255,255,255,0.4)' }}>No completed assessments.</div>
                      : ur.map((r, i) => {
                          const risk = getRisk(r.score);
                          return (
                            <div key={i} style={{ ...card, marginBottom: 12 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div>
                                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{r.assessmentType === 'tabletop' ? 'Privacy Tabletop' : 'Privacy Engineering'}</div>
                                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{new Date(r.completedAt).toLocaleDateString()}</div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ fontSize: 26, fontWeight: 700, color: risk.color }}>{r.score}</div>
                                  {pill(risk.color, risk.label + ' Risk')}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ASSESSMENTS */}
          {entTab === 'assessments' && (
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 20 }}>Completed assessments</div>
              {completed.length === 0 ? <div style={{ ...card, textAlign: 'center', padding: 48, color: 'rgba(255,255,255,0.35)' }}>No completed assessments yet.</div>
                : (
                  <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                          {['User', 'Assessment', 'Score', 'Risk', 'Date'].map(h => (
                            <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, textTransform: 'uppercase' as const, letterSpacing: 1, color: 'rgba(255,255,255,0.35)', fontWeight: 600 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {[...completed].sort((a, b) => b.completedAt.localeCompare(a.completedAt)).map((r, i) => {
                          const risk = getRisk(r.score);
                          return (
                            <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                              <td style={{ padding: '12px 16px', fontSize: 12, color: '#818cf8', fontFamily: 'monospace' }}>{r.userId.slice(0, 8)}…</td>
                              <td style={{ padding: '12px 16px', fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>{r.assessmentType === 'tabletop' ? 'Privacy Tabletop' : 'Privacy Engineering'}</td>
                              <td style={{ padding: '12px 16px', fontSize: 16, fontWeight: 700, color: risk.color }}>{r.score}</td>
                              <td style={{ padding: '12px 16px' }}>{pill(risk.color, risk.label)}</td>
                              <td style={{ padding: '12px 16px', fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>{new Date(r.completedAt).toLocaleDateString()}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
            </div>
          )}

          {/* ENTERPRISE SUPER ADMINS */}
          {entTab === 'super_admins' && (
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 6 }}>Enterprise Super Admins</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', marginBottom: 24 }}>
                Users with full super admin access to this portal. Changes take effect on next sign-in.
              </div>
              <div style={{ ...card, marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#a5b4fc', marginBottom: 14 }}>Add Super Admin</div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <input type="email" placeholder="Email (must have an existing enterprise account)" value={newEntAdminEmail} onChange={e => { setNewEntAdminEmail(e.target.value); setAddEntAdminStatus(null); }}
                    style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '9px 14px', color: '#fff', fontSize: 13, outline: 'none' }} />
                  <button disabled={addEntAdminLoading || !newEntAdminEmail.trim()} onClick={async () => {
                    setAddEntAdminLoading(true); setAddEntAdminStatus(null);
                    const r = await addSuperAdmin(newEntAdminEmail.trim());
                    if (r.success) { setAddEntAdminStatus({ type: 'success', msg: `${newEntAdminEmail} added.` }); setNewEntAdminEmail(''); loadEntSuperAdmins(); }
                    else setAddEntAdminStatus({ type: 'error', msg: r.error || 'Failed.' });
                    setAddEntAdminLoading(false);
                  }} style={{ background: 'rgba(129,140,248,0.15)', border: '1px solid rgba(129,140,248,0.4)', color: '#a5b4fc', padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    {addEntAdminLoading ? 'Adding…' : 'Add'}
                  </button>
                </div>
                {addEntAdminStatus && <div style={{ marginTop: 10, fontSize: 13, color: addEntAdminStatus.type === 'success' ? '#a5b4fc' : '#f87171' }}>{addEntAdminStatus.msg}</div>}
              </div>

              {entSuperAdminsLoading ? <div style={{ textAlign: 'center', padding: 48 }}><Spinner color="#818cf8" /></div>
                : entSuperAdmins.length === 0 ? <div style={{ ...card, textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.35)', fontSize: 13 }}>No super admins found.</div>
                : (
                  <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                          {['Email', 'Status', 'Added', ''].map(h => (
                            <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, textTransform: 'uppercase' as const, letterSpacing: 1, color: 'rgba(255,255,255,0.35)', fontWeight: 600 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {entSuperAdmins.map((admin, i) => {
                          const isMe = admin.email === myEmail;
                          return (
                            <tr key={admin.userId} style={{ borderBottom: i < entSuperAdmins.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none', background: isMe ? 'rgba(129,140,248,0.04)' : 'transparent' }}>
                              <td style={{ padding: '13px 16px', fontSize: 13 }}>{admin.email}{isMe && <span style={{ marginLeft: 8, fontSize: 10, color: '#a5b4fc', fontWeight: 700 }}>YOU</span>}</td>
                              <td style={{ padding: '13px 16px' }}>{pill(admin.enabled ? '#22c55e' : '#ef4444', admin.enabled ? 'Active' : 'Disabled')}</td>
                              <td style={{ padding: '13px 16px', fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{new Date(admin.createdAt).toLocaleDateString()}</td>
                              <td style={{ padding: '13px 16px', textAlign: 'right' }}>
                                {!isMe && (
                                  <button onClick={async () => { if (!window.confirm(`Remove ${admin.email} as super admin?`)) return; const r = await removeSuperAdmin(admin.userId); if (r.success) setEntSuperAdmins(prev => prev.filter(a => a.userId !== admin.userId)); }}
                                    style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', padding: '5px 14px', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Remove</button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
            </div>
          )}
        </>
      )}

      {/* ══════ PUBLIC ══════ */}
      {platform === 'public' && (
        <>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' as const, marginBottom: 28 }}>
            {pubTabBtn('orgs',        'Organisations')}
            {pubTabBtn('users',       `Users${publicUsers.length ? ` (${publicUsers.length})` : ''}`)}
            {pubTabBtn('super_admins', 'Super Admins')}
          </div>

          {/* PUBLIC ORGS LIST */}
          {pubTab === 'orgs' && !selectedPublicOrg && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div style={{ fontSize: 17, fontWeight: 700 }}>
                  Public Organisations {publicOrgs.length > 0 && <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>({publicOrgs.length})</span>}
                </div>
                <button onClick={() => setShowPublicOrgWizard(true)} style={{ background: 'linear-gradient(135deg,#10b981,#059669)', border: 'none', borderRadius: 10, color: '#fff', fontSize: 13, fontWeight: 700, padding: '9px 18px', cursor: 'pointer' }}>
                  + New Organisation
                </button>
              </div>
              {publicOrgsLoading ? <div style={{ textAlign: 'center', padding: 48 }}><Spinner color="#34d399" /></div>
                : publicOrgs.length === 0 ? <div style={{ ...card, textAlign: 'center', padding: 48, color: 'rgba(255,255,255,0.35)' }}>No public organisations yet.</div>
                : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {publicOrgs.map(org => {
                      const planColor   = org.plan === 'enterprise' ? '#a78bfa' : org.plan === 'professional' ? '#60a5fa' : '#34d399';
                      const statusColor = org.status === 'active' ? '#22c55e' : org.status === 'trial' ? '#f97316' : '#ef4444';
                      const seatsPct    = org.seats === -1 ? 0 : (org.seatsUsed / org.seats) * 100;
                      return (
                        <div key={org.orgId} style={{ ...card, padding: '16px 20px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' as const, gap: 10 }}>
                            <div style={{ cursor: 'pointer', flex: 1 }} onClick={() => { setSelectedPublicOrg(org); setPubOrgUsers([]); setResendStatus(null); setPubOrgUsersLoading(true); getPublicOrgUsers(org.orgId).then(u => { setPubOrgUsers(u); setPubOrgUsersLoading(false); }); }}>
                              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>{org.name}</div>
                              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>
                                {org.domain || 'No domain'} · Code: <span style={{ fontFamily: 'monospace', color: '#6ee7b7', fontWeight: 700 }}>{org.joinCode}</span>
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' as const }}>
                              {pill(planColor, org.plan.charAt(0).toUpperCase() + org.plan.slice(1))}
                              {pill(statusColor, org.status.charAt(0).toUpperCase() + org.status.slice(1))}
                              <div style={{ textAlign: 'center', minWidth: 60 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#34d399' }}>{org.seats === -1 ? `${org.seatsUsed} / ∞` : `${org.seatsUsed} / ${org.seats}`}</div>
                                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>seats</div>
                                {org.seats !== -1 && <div style={{ height: 3, width: 60, background: 'rgba(255,255,255,0.08)', borderRadius: 2, marginTop: 3 }}><div style={{ height: '100%', width: `${seatsPct}%`, background: seatsPct > 90 ? '#ef4444' : '#34d399', borderRadius: 2 }} /></div>}
                              </div>
                              <div style={{ cursor: 'pointer', fontSize: 12, color: '#34d399' }} onClick={() => { setSelectedPublicOrg(org); setPubOrgUsers([]); setResendStatus(null); setPubOrgUsersLoading(true); getPublicOrgUsers(org.orgId).then(u => { setPubOrgUsers(u); setPubOrgUsersLoading(false); }); }}>View →</div>
                              <button onClick={async e => { e.stopPropagation(); if (!window.confirm(`Delete "${org.name}" and all users?`)) return; const res = await deletePublicOrg(org.orgId); if (res.success) setPublicOrgs(prev => prev.filter(o => o.orgId !== org.orgId)); }}
                                style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.35)', color: '#f87171', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 6, cursor: 'pointer' }}>Delete</button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
            </div>
          )}

          {/* PUBLIC ORG DETAIL */}
          {pubTab === 'orgs' && selectedPublicOrg && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <button onClick={() => setSelectedPublicOrg(null)} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', padding: '8px 16px', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>← Back</button>
                <button onClick={async () => { if (!window.confirm(`Delete "${selectedPublicOrg.name}" and all users?`)) return; const res = await deletePublicOrg(selectedPublicOrg.orgId); if (res.success) { setPublicOrgs(prev => prev.filter(o => o.orgId !== selectedPublicOrg.orgId)); setSelectedPublicOrg(null); } }}
                  style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.4)', color: '#f87171', padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Delete Organisation</button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 20, marginBottom: 16 }}>
                <div style={card}>
                  <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 3 }}>{selectedPublicOrg.name}</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginBottom: 16 }}>{selectedPublicOrg.domain || 'No domain'}</div>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: 1 }}>Join Code</div>
                    <div style={{ fontFamily: 'monospace', fontSize: 22, fontWeight: 800, letterSpacing: '0.15em', color: '#6ee7b7' }}>{selectedPublicOrg.joinCode}</div>
                  </div>
                  {[
                    ['Plan', selectedPublicOrg.plan.charAt(0).toUpperCase() + selectedPublicOrg.plan.slice(1)],
                    ['Seats', selectedPublicOrg.seats === -1 ? `${selectedPublicOrg.seatsUsed} (∞)` : `${selectedPublicOrg.seatsUsed} / ${selectedPublicOrg.seats}`],
                    ['Status', selectedPublicOrg.status.charAt(0).toUpperCase() + selectedPublicOrg.status.slice(1)],
                    ['Billing', selectedPublicOrg.billingEmail || '—'],
                    ['Created', new Date(selectedPublicOrg.createdAt).toLocaleDateString()],
                  ].map(([l, v]) => (
                    <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.05)', fontSize: 13 }}>
                      <span style={{ color: 'rgba(255,255,255,0.4)' }}>{l}</span><span style={{ fontWeight: 600 }}>{v}</span>
                    </div>
                  ))}
                </div>
                <div style={card}>
                  <div style={{ fontSize: 11, textTransform: 'uppercase' as const, letterSpacing: 2, color: '#34d399', marginBottom: 14, fontWeight: 700 }}>Update License</div>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' as const, alignItems: 'flex-end' }}>
                    {[
                      { label: 'Plan', field: 'plan' as const, opts: PLAN_OPTS },
                      { label: 'Status', field: 'status' as const, opts: STATUS_OPTS },
                    ].map(({ label, field, opts }) => (
                      <div key={field}>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 5, fontWeight: 600 }}>{label}</div>
                        <select defaultValue={(selectedPublicOrg as any)[field]} onChange={e => setSelectedPublicOrg(p => p ? { ...p, [field]: e.target.value } : p)}
                          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '8px 12px', color: '#fff', fontSize: 13, outline: 'none' }}>
                          {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>
                    ))}
                    <div>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 5, fontWeight: 600 }}>Seats (-1 = ∞)</div>
                      <input type="number" defaultValue={selectedPublicOrg.seats} min={-1} onChange={e => setSelectedPublicOrg(p => p ? { ...p, seats: Number(e.target.value) } : p)}
                        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '8px 12px', color: '#fff', fontSize: 13, outline: 'none', width: 80, fontFamily: 'inherit' }} />
                    </div>
                    <button onClick={async () => { const res = await updatePublicOrg(selectedPublicOrg.orgId, { plan: selectedPublicOrg.plan, seats: selectedPublicOrg.seats, status: selectedPublicOrg.status }); if (res.success) setPublicOrgs(prev => prev.map(o => o.orgId === selectedPublicOrg.orgId ? selectedPublicOrg : o)); }}
                      style={{ background: 'linear-gradient(135deg,#10b981,#059669)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 700, padding: '9px 18px', cursor: 'pointer' }}>Save</button>
                  </div>
                </div>
              </div>

              {/* Members */}
              <div style={{ ...card }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showPubOrgInvite ? 16 : 0 }}>
                  <div>
                    <div style={{ fontSize: 11, textTransform: 'uppercase' as const, letterSpacing: 2, color: '#34d399', fontWeight: 700 }}>Members</div>
                    {selectedPublicOrg.seats !== -1 && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>{selectedPublicOrg.seats - selectedPublicOrg.seatsUsed} seats available</div>}
                  </div>
                  <button onClick={() => { setShowPubOrgInvite(v => !v); setPubInviteStatus(null); }}
                    style={{ background: showPubOrgInvite ? 'rgba(255,255,255,0.06)' : 'rgba(16,185,129,0.15)', border: `1px solid ${showPubOrgInvite ? 'rgba(255,255,255,0.12)' : 'rgba(16,185,129,0.4)'}`, color: showPubOrgInvite ? 'rgba(255,255,255,0.5)' : '#34d399', fontSize: 12, fontWeight: 700, padding: '6px 14px', borderRadius: 8, cursor: 'pointer' }}>
                    {showPubOrgInvite ? 'Cancel' : '+ Invite User'}
                  </button>
                </div>

                {showPubOrgInvite && (
                  selectedPublicOrg.seats !== -1 && selectedPublicOrg.seatsUsed >= selectedPublicOrg.seats ? (
                    <div style={{ padding: '12px 16px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', fontSize: 13, marginBottom: 16 }}>No seats available. Update the seat limit first.</div>
                  ) : (
                    <form onSubmit={async e => {
                      e.preventDefault(); setPubInviteLoading(true); setPubInviteStatus(null);
                      const res = await invitePublicOrgUser(selectedPublicOrg.orgId, pubInviteEmail.trim(), pubInviteFirst.trim(), pubInviteLast.trim(), pubInviteRole);
                      setPubInviteLoading(false);
                      if (res.success) {
                        setPubInviteStatus({ type: 'success', msg: `Invite sent to ${pubInviteEmail}` });
                        setPubInviteEmail(''); setPubInviteFirst(''); setPubInviteLast(''); setPubInviteRole('user');
                        setSelectedPublicOrg(p => p ? { ...p, seatsUsed: p.seatsUsed + 1 } : p);
                        setPublicOrgs(prev => prev.map(o => o.orgId === selectedPublicOrg.orgId ? { ...o, seatsUsed: o.seatsUsed + 1 } : o));
                      } else {
                        setPubInviteStatus({ type: 'error', msg: res.error || 'Failed.' });
                      }
                    }} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                      {[
                        { label: 'Email *', value: pubInviteEmail, setter: setPubInviteEmail, type: 'email', placeholder: 'user@org.com', required: true },
                        { label: 'First name', value: pubInviteFirst, setter: setPubInviteFirst, type: 'text', placeholder: 'Jane', required: false },
                        { label: 'Last name', value: pubInviteLast, setter: setPubInviteLast, type: 'text', placeholder: 'Smith', required: false },
                      ].map(({ label, value, setter, type, placeholder, required }) => (
                        <div key={label}>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 5, fontWeight: 600 }}>{label}</div>
                          <input type={type} value={value} onChange={e => setter(e.target.value)} placeholder={placeholder} required={required}
                            style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 13, outline: 'none', fontFamily: 'inherit' }} />
                        </div>
                      ))}
                      <div>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 5, fontWeight: 600 }}>Role</div>
                        {sel(pubInviteRole, setPubInviteRole, [{ value: 'user', label: 'User' }, { value: 'org_admin', label: 'Org Admin' }])}
                      </div>
                      <div style={{ gridColumn: '1/-1' }}>
                        <StatusMsg status={pubInviteStatus} />
                        <button type="submit" disabled={pubInviteLoading} style={{ background: pubInviteLoading ? 'rgba(16,185,129,0.3)' : 'linear-gradient(135deg,#10b981,#059669)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 700, padding: '10px 24px', cursor: pubInviteLoading ? 'not-allowed' : 'pointer', marginTop: pubInviteStatus ? 10 : 0 }}>
                          {pubInviteLoading ? 'Sending…' : 'Send Invite'}
                        </button>
                      </div>
                    </form>
                  )
                )}

                {pubOrgUsersLoading ? <div style={{ textAlign: 'center', padding: 24 }}><Spinner color="#34d399" /></div>
                  : pubOrgUsers.length === 0 ? <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>No members yet.</div>
                  : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                          {['Email', 'Name', 'Role', 'Status', 'Invited', ''].map(h => (
                            <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, textTransform: 'uppercase' as const, letterSpacing: 1, color: 'rgba(255,255,255,0.3)', fontWeight: 600 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {pubOrgUsers.map((u, i) => {
                          const pending   = u.status === 'FORCE_CHANGE_PASSWORD' || u.status === 'invited' || u.status === 'pending';
                          const myResend  = resendStatus?.userId === u.userId ? resendStatus : null;
                          return (
                            <tr key={u.userId} style={{ borderBottom: i < pubOrgUsers.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                              <td style={{ padding: '10px 12px', fontSize: 13, color: 'rgba(255,255,255,0.8)' }}>{u.email}</td>
                              <td style={{ padding: '10px 12px', fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>{[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}</td>
                              <td style={{ padding: '10px 12px' }}>{pill(u.role === 'org_admin' ? '#a5b4fc' : '#6ee7b7', u.role === 'org_admin' ? 'Admin' : 'User')}</td>
                              <td style={{ padding: '10px 12px' }}>{pill(pending ? '#f97316' : '#22c55e', pending ? 'Pending' : 'Active')}</td>
                              <td style={{ padding: '10px 12px', fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>{u.invitedAt ? new Date(u.invitedAt).toLocaleDateString() : '—'}</td>
                              <td style={{ padding: '10px 12px' }}>
                                {myResend ? <span style={{ fontSize: 11, color: myResend.type === 'success' ? '#34d399' : '#f87171' }}>{myResend.msg}</span>
                                  : <button onClick={async () => { setResendStatus(null); const res = await resendPublicOrgInvite(selectedPublicOrg!.orgId, u.userId); setResendStatus({ userId: u.userId, type: res.success ? 'success' : 'error', msg: res.success ? 'Resent' : (res.error || 'Failed') }); }}
                                    style={{ background: 'rgba(129,140,248,0.1)', border: '1px solid rgba(129,140,248,0.3)', color: '#a5b4fc', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 6, cursor: 'pointer' }}>Resend</button>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
              </div>
            </div>
          )}

          {/* PUBLIC USERS */}
          {pubTab === 'users' && (
            publicLoading ? <div style={{ textAlign: 'center', padding: 80 }}><Spinner color="#34d399" /></div>
              : (
                <>
                  {(() => {
                    const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000);
                    const newThisMonth  = publicUsers.filter(u => new Date(u.createdAt) > thirtyDaysAgo).length;
                    const assigned      = publicUsers.filter(u => u.orgId).length;
                    return (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 14, marginBottom: 24 }}>
                        {[
                          { label: 'Total registered', value: publicUsers.length, color: '#34d399' },
                          { label: 'New in 30 days',   value: newThisMonth,       color: '#34d399' },
                          { label: 'In an org',        value: assigned,           color: '#818cf8' },
                          { label: 'Unassigned',       value: publicUsers.length - assigned, color: '#f97316' },
                        ].map((s, i) => (
                          <div key={i} style={card}>
                            <div style={{ fontSize: 28, fontWeight: 700, color: s.color }}>{s.value}</div>
                            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>{s.label}</div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                  {publicUsers.length === 0 ? <div style={{ ...card, textAlign: 'center', padding: 48, color: 'rgba(255,255,255,0.35)' }}>No users yet.</div>
                    : (
                      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                              {['Email', 'Joined', 'Status', 'Plan', 'Organisation', 'Assign', ''].map(h => (
                                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, textTransform: 'uppercase' as const, letterSpacing: 1, color: 'rgba(255,255,255,0.35)', fontWeight: 600 }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {[...publicUsers].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).map((u, i) => {
                              const statusColor      = u.status === 'CONFIRMED' ? '#22c55e' : '#f97316';
                              const isAssigning      = assigningUser === u.userId;
                              const myAssignStatus   = assignStatus?.userId === u.userId ? assignStatus : null;
                              const isOverriding     = overridingPlanUser === u.userId;
                              const myPlanStatus     = overridePlanStatus?.userId === u.userId ? overridePlanStatus : null;
                              const userPlan         = (u as any).plan as string | undefined;
                              return (
                                <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                  <td style={{ padding: '12px 16px', fontSize: 13, color: 'rgba(255,255,255,0.8)' }}>{u.email || <span style={{ color: 'rgba(255,255,255,0.3)' }}>—</span>}</td>
                                  <td style={{ padding: '12px 16px', fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{new Date(u.createdAt).toLocaleDateString()}</td>
                                  <td style={{ padding: '12px 16px' }}>{pill(statusColor, u.status)}</td>
                                  <td style={{ padding: '12px 16px' }}>
                                    {myPlanStatus ? <span style={{ fontSize: 12, color: myPlanStatus.type === 'success' ? '#22c55e' : '#ef4444' }}>{myPlanStatus.msg}</span>
                                      : isOverriding ? (
                                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                          <select value={overridePlanValue} onChange={e => setOverridePlanValue(e.target.value as 'paid' | 'free')} style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, padding: '5px 8px', color: '#fff', fontSize: 12, outline: 'none' }}>
                                            <option value="paid">Paid</option><option value="free">Free</option>
                                          </select>
                                          <button onClick={async () => { const res = await overridePublicUserPlan(u.userId, overridePlanValue); setOverridePlanStatus({ userId: u.userId, type: res.success ? 'success' : 'error', msg: res.success ? `Set to ${overridePlanValue}` : (res.error || 'Failed') }); setOverridingPlanUser(null); }} style={{ background: 'rgba(52,211,153,0.15)', border: '1px solid rgba(52,211,153,0.3)', color: '#34d399', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 6, cursor: 'pointer' }}>Save</button>
                                          <button onClick={() => setOverridingPlanUser(null)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', fontSize: 12, cursor: 'pointer' }}>✕</button>
                                        </div>
                                      ) : (
                                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                          {pill(userPlan === 'paid' ? '#22c55e' : '#64748b', userPlan || 'free')}
                                          <button onClick={() => { setOverridingPlanUser(u.userId); setOverridePlanValue((userPlan as 'paid' | 'free') || 'free'); setOverridePlanStatus(null); }} style={{ background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.3)', color: '#fbbf24', fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6, cursor: 'pointer' }}>Override</button>
                                        </div>
                                      )}
                                  </td>
                                  <td style={{ padding: '12px 16px', fontSize: 12, color: u.orgId ? '#a5b4fc' : 'rgba(255,255,255,0.25)' }}>
                                    {u.orgId ? publicOrgs.find(o => o.orgId === u.orgId)?.name || u.orgId : 'Unassigned'}
                                  </td>
                                  <td style={{ padding: '12px 16px' }}>
                                    {myAssignStatus ? <span style={{ fontSize: 12, color: myAssignStatus.type === 'success' ? '#22c55e' : '#ef4444' }}>{myAssignStatus.msg}</span>
                                      : isAssigning ? (
                                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                          <select value={assignOrgId} onChange={e => setAssignOrgId(e.target.value)} style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, padding: '5px 8px', color: '#fff', fontSize: 12, outline: 'none' }}>
                                            <option value="">Select org…</option>
                                            {publicOrgs.map(o => <option key={o.orgId} value={o.orgId}>{o.name}</option>)}
                                          </select>
                                          <select value={assignRole} onChange={e => setAssignRole(e.target.value)} style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, padding: '5px 8px', color: '#fff', fontSize: 12, outline: 'none' }}>
                                            <option value="user">User</option><option value="org_admin">Admin</option>
                                          </select>
                                          <button onClick={async () => { if (!assignOrgId) return; const res = await assignUserToPublicOrg(assignOrgId, u.userId, u.email, assignRole); setAssignStatus({ userId: u.userId, type: res.success ? 'success' : 'error', msg: res.success ? 'Assigned' : (res.error || 'Failed') }); if (res.success) setPublicUsers(prev => prev.map(x => x.userId === u.userId ? { ...x, orgId: assignOrgId, orgRole: assignRole } : x)); setAssigningUser(null); }} style={{ background: 'rgba(52,211,153,0.15)', border: '1px solid rgba(52,211,153,0.3)', color: '#34d399', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 6, cursor: 'pointer' }}>Save</button>
                                          <button onClick={() => setAssigningUser(null)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', fontSize: 12, cursor: 'pointer' }}>✕</button>
                                        </div>
                                      ) : (
                                        <button onClick={() => { setAssigningUser(u.userId); setAssignOrgId(''); setAssignRole('user'); setAssignStatus(null); if (publicOrgs.length === 0) { setPublicOrgsLoading(true); getPublicOrgs().then(o => { setPublicOrgs(o); setPublicOrgsLoading(false); }); } }}
                                          style={{ background: 'rgba(129,140,248,0.1)', border: '1px solid rgba(129,140,248,0.3)', color: '#a5b4fc', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 6, cursor: 'pointer' }}>
                                          {u.orgId ? 'Move' : 'Assign'}
                                        </button>
                                      )}
                                  </td>
                                  <td style={{ padding: '12px 16px' }}>
                                    <button onClick={async () => { if (!window.confirm(`Delete ${u.email}?`)) return; const res = await deletePublicUser(u.userId); if (res.success) setPublicUsers(prev => prev.filter(x => x.userId !== u.userId)); }}
                                      style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.5)', color: '#f87171', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 6, cursor: 'pointer' }}>Delete</button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                </>
              )
          )}

          {/* PUBLIC SUPER ADMINS */}
          {pubTab === 'super_admins' && (
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 6 }}>Public Platform Super Admins</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', marginBottom: 24 }}>
                Users with full super admin access to the public platform. Changes take effect on next sign-in.
              </div>
              <div style={{ ...card, marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#6ee7b7', marginBottom: 14 }}>Add Super Admin</div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <input type="email" placeholder="Email (must have an existing public platform account)" value={newPubAdminEmail} onChange={e => { setNewPubAdminEmail(e.target.value); setAddPubAdminStatus(null); }}
                    style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '9px 14px', color: '#fff', fontSize: 13, outline: 'none' }} />
                  <button disabled={addPubAdminLoading || !newPubAdminEmail.trim()} onClick={async () => {
                    setAddPubAdminLoading(true); setAddPubAdminStatus(null);
                    const r = await addPublicSuperAdmin(newPubAdminEmail.trim());
                    if (r.success) { setAddPubAdminStatus({ type: 'success', msg: `${newPubAdminEmail} added.` }); setNewPubAdminEmail(''); loadPubSuperAdmins(); }
                    else setAddPubAdminStatus({ type: 'error', msg: r.error || 'Failed.' });
                    setAddPubAdminLoading(false);
                  }} style={{ background: 'rgba(52,211,153,0.15)', border: '1px solid rgba(52,211,153,0.4)', color: '#34d399', padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    {addPubAdminLoading ? 'Adding…' : 'Add'}
                  </button>
                </div>
                {addPubAdminStatus && <div style={{ marginTop: 10, fontSize: 13, color: addPubAdminStatus.type === 'success' ? '#34d399' : '#f87171' }}>{addPubAdminStatus.msg}</div>}
              </div>

              {pubSuperAdminsLoading ? <div style={{ textAlign: 'center', padding: 48 }}><Spinner color="#34d399" /></div>
                : pubSuperAdmins.length === 0 ? <div style={{ ...card, textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.35)', fontSize: 13 }}>No super admins found.</div>
                : (
                  <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                          {['Email', 'Status', 'Added', ''].map(h => (
                            <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, textTransform: 'uppercase' as const, letterSpacing: 1, color: 'rgba(255,255,255,0.35)', fontWeight: 600 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {pubSuperAdmins.map((admin, i) => {
                          const isMe = admin.email === myEmail;
                          return (
                            <tr key={admin.userId} style={{ borderBottom: i < pubSuperAdmins.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none', background: isMe ? 'rgba(52,211,153,0.04)' : 'transparent' }}>
                              <td style={{ padding: '13px 16px', fontSize: 13 }}>{admin.email}{isMe && <span style={{ marginLeft: 8, fontSize: 10, color: '#34d399', fontWeight: 700 }}>YOU</span>}</td>
                              <td style={{ padding: '13px 16px' }}>{pill(admin.enabled ? '#22c55e' : '#ef4444', admin.enabled ? 'Active' : 'Disabled')}</td>
                              <td style={{ padding: '13px 16px', fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{new Date(admin.createdAt).toLocaleDateString()}</td>
                              <td style={{ padding: '13px 16px', textAlign: 'right' }}>
                                {!isMe && (
                                  <button onClick={async () => { if (!window.confirm(`Remove ${admin.email} as super admin?`)) return; const r = await removePublicSuperAdmin(admin.userId); if (r.success) setPubSuperAdmins(prev => prev.filter(a => a.userId !== admin.userId)); }}
                                    style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', padding: '5px 14px', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Remove</button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
